"use client"

import { useState, useEffect } from "react"
import { FaEnvelope, FaInstagram, FaFacebook, FaLinkedin, FaMapMarkerAlt, FaUsers, FaBuilding } from "react-icons/fa"

const ContactUs = () => {
  const [visibleSections, setVisibleSections] = useState(new Set())

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]))
          }
        })
      },
      { threshold: 0.1 },
    )

    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const socialLinks = [
    {
      name: "Instagram",
      url: "https://instagram.com/iiche_avvu?igshid=MzRlODBiNWFlZA==",
      icon: FaInstagram,
      color: "text-pink-500 hover:text-pink-600",
      bgColor: "hover:bg-pink-50 dark:hover:bg-pink-900/20",
    },
    {
      name: "Facebook",
      url: "https://www.facebook.com/profile.php?id=61550312694931&mibextid=ZbWKwL",
      icon: FaFacebook,
      color: "text-blue-600 hover:text-blue-700",
      bgColor: "hover:bg-blue-50 dark:hover:bg-blue-900/20",
    },
    {
      name: "LinkedIn",
      url: "https://www.linkedin.com/company/iiche-avvu/",
      icon: FaLinkedin,
      color: "text-blue-700 hover:text-blue-800",
      bgColor: "hover:bg-blue-50 dark:hover:bg-blue-900/20",
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div
          data-animate
          id="header"
          className={`text-center mb-12 transform transition-all duration-700 ${
            visibleSections.has("header") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">Contact Us</h1>
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-blue-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Get in touch with the IIChE-AVVU Student Chapter
          </p>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-8 mb-12">
          {/* Contact Information */}
          <div
            data-animate
            id="contact-info"
            className={`transform transition-all duration-700 ${
              visibleSections.has("contact-info") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">Contact Information</h2>

              {/* Organization */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaBuilding className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Organization</h3>
                  <p className="text-gray-600 dark:text-gray-300">IIChE-AVVU Student Chapter, Coimbatore</p>
                </div>
              </div>

              {/* Faculty Coordinators */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaUsers className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Faculty Coordinators</h3>
                  <div className="text-gray-600 dark:text-gray-300 space-y-1">
                    <p>Amal G.S</p>
                    <p>Hareesh P</p>
                    <p>Sindhu S</p>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Assistant Professors, Department of Chemical Engineering & Materials Science
                  </p>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start space-x-4 mb-6">
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaMapMarkerAlt className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Location</h3>
                  <p className="text-gray-600 dark:text-gray-300">
                    Amrita Vishwa Vidyapeetham
                    <br />
                    Coimbatore Campus
                  </p>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FaEnvelope className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Email</h3>
                  <a
                    href="mailto:iiche@cb.amrita.edu"
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200"
                  >
                    iiche@cb.amrita.edu
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div
            data-animate
            id="map"
            className={`transform transition-all duration-700 ${
              visibleSections.has("map") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Find Us</h2>
              </div>
              <div className="relative">
                <iframe
                  title="Amrita Vishwa Vidyapeetham Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3917.801579878136!2d76.89805297472645!3d10.902679089253887!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ba85c95d3e828eb%3A0x2785cb4510629029!2sAmrita%20Vishwa%20Vidyapeetham%2C%20Coimbatore%20campus!5e0!3m2!1sen!2sin!4v1691759212413!5m2!1sen!2sin"
                  width="100%"
                  height="400"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  className="w-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media Section */}
        <div
          data-animate
          id="social"
          className={`transform transition-all duration-700 ${
            visibleSections.has("social") ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
          }`}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Follow Us</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-8">Stay connected with our latest updates</p>

              <div className="flex justify-center items-center space-x-6">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group flex items-center justify-center w-14 h-14 rounded-full border-2 border-gray-200 dark:border-gray-600 ${social.color} ${social.bgColor} transition-all duration-300 hover:border-current hover:scale-110`}
                    title={social.name}
                  >
                    <social.icon className="w-6 h-6" />
                  </a>
                ))}
              </div>

              {/* Direct Email Button */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <a
                  href="mailto:iiche@cb.amrita.edu"
                  className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200"
                >
                  <FaEnvelope className="w-4 h-4" />
                  <span>Send Email</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ContactUs
